/**
 * HorizonScan.jsx: BM-H3302 (30 Sep 2026).
 *
 * Vanessa, 30 Sep 2026: run a horizon scan on demand and on a schedule she
 * sets, SRA, Law Society and Lexcel first. MLCO and COLP only.
 * Server: /api/compliance/horizon-scan (backend/compliance/horizon_scan_router.py).
 *
 * The page never shows a quiet "nothing new" for a scan that read nothing:
 * every run shows how many sources were read, which failed and why, and which
 * sources must be checked by hand because their sites refuse automated readers.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Radar, Play, AlertTriangle, ExternalLink, Sparkles, Loader2 } from "lucide-react";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

function authHeaders() {
  const email =
    sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
  return email ? { "X-Bonsai-User-Id": email } : {};
}

const BASE = `${API}/compliance/horizon-scan`;
const INPUT =
  "px-2 py-1 rounded-md text-sm bg-slate-900/60 border border-slate-700/60 text-slate-200 disabled:opacity-70";
const STATUS = {
  complete: ["Complete", "bg-emerald-900/50 text-emerald-200"],
  complete_with_problems: ["Complete, with problems", "bg-amber-900/50 text-amber-200"],
  failed: ["Failed", "bg-red-900/50 text-red-200"],
  running: ["Running", "bg-sky-900/50 text-sky-200"],
  // BM-H3817: a run that stopped part way (a restart, or work cut off) is named, not hidden.
  interrupted: ["Interrupted, run again", "bg-red-900/50 text-red-200"],
};
const RELEVANCE = {
  high: "bg-red-900/50 text-red-200", medium: "bg-amber-900/50 text-amber-200",
  low: "bg-slate-800 text-slate-300", none: "bg-slate-800 text-slate-500",
};
const TRIAGE = [["assess", "Assess"], ["monitor", "Monitor"], ["no_action", "No action"], ["out_of_scope", "Out of scope"]];

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "");
const hourLabel = (h) => `${String(h).padStart(2, "0")}:00`;

function RunSummary({ run }) {
  const [label, cls] = STATUS[run.status] || [run.status, "bg-slate-800 text-slate-300"];
  return (
    <div className="text-sm text-slate-200 space-y-1" data-testid="hs-run-summary">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`px-2 py-0.5 rounded-full text-xs ${cls}`}>{label}</span>
        <span>{run.sources_ok ?? 0} of {run.sources_total ?? 0} sources read</span>
        <span className="text-slate-400">· {run.items_new ?? 0} new item{run.items_new === 1 ? "" : "s"}</span>
        {run.sources_baselined ? (
          <span className="text-slate-400">· {run.sources_baselined} read for the first time (baseline, nothing reported)</span>
        ) : null}
      </div>
      {run.error ? <div className="text-red-300 text-xs">{run.error}</div> : null}
      {run.suggestion_error ? (
        <div className="text-amber-300 text-xs">Policy suggestions could not be made: {run.suggestion_error}. The items are saved.</div>
      ) : null}
    </div>
  );
}

export default function HorizonScan() {
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saved, setSaved] = useState("");
  const [reasons, setReasons] = useState({});
  // BM-H3980: Action puts an item's policies into the open change pack.
  const [policies, setPolicies] = useState([]);
  const [acting, setActing] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [notice, setNotice] = useState("");
  const [log, setLog] = useState(null);
  // BM-H4025 (Vanessa, 8 Oct 2026): "i only want results that have a suggestion in them".
  const [showOthers, setShowOthers] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  // 24 Oct 2026 (Vanessa): Claude suggests the reason here too. It only fills the box.
  const [reasonBusy, setReasonBusy] = useState(null);
  // BM-H4049 (Vanessa, 8 Oct 2026): "the buttons don't seem to do anything". Their
  // messages went to the top of the page, out of sight below a long list. Each
  // message now appears beside the item or button that was pressed.
  const [itemMsg, setItemMsg] = useState({});
  const [listMsg, setListMsg] = useState(null);
  const sayAt = (id, kind, text) => setItemMsg((m) => ({ ...m, [id]: { kind, text } }));
  const needReason = (id) => {
    sayAt(id, "error", "Write a reason in the box first, then press the button again.");
    const box = document.getElementById(`hs-reason-${id}`);
    if (box) box.focus();
  };

  const suggestReason = async (id) => {
    setReasonBusy(id);
    try {
      const r = await axios.post(`${API}/policy-change-packs/scan-items/${id}/suggest-reason`,
        { policies: acting === id ? chosen : [] }, { headers: authHeaders() });
      setReasons((prev) => ({ ...prev, [id]: r.data.reason || "" }));
      sayAt(id, "ok", "Suggested by Claude. Check and edit it before you press a button.");
    } catch (err) {
      sayAt(id, "error", errorMessage(err, "Claude could not suggest a reason"));
    } finally {
      setReasonBusy(null);
    }
  };

  const suggestMissing = async () => {
    setSuggesting(true);
    setListMsg({ kind: "info", text: "Asking Claude. This can take a minute or two; keep this page open." });
    try {
      const r = await axios.post(`${BASE}/suggest-missing`, {}, { headers: authHeaders() });
      const { examined, suggested, error: failed } = r.data;
      await load();
      setListMsg(failed
        ? { kind: "error", text: `Claude suggested policies for ${suggested} of ${examined} item(s), then stopped: ${failed}` }
        : { kind: "ok", text: `Claude suggested policies for ${suggested} of ${examined} item(s) that had none.` });
    } catch (err) {
      setListMsg({ kind: "error", text: errorMessage(err, "Could not ask Claude for suggestions") });
    } finally {
      setSuggesting(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/policy-change-packs/policies`, { headers: authHeaders() })
      .then((r) => setPolicies(r.data.policies || []))
      .catch(() => setPolicies([]));
  }, []);

  const startAction = (it) => {
    const suggested = (it.suggestion?.policies || []);
    setChosen(policies.filter((p) => suggested.includes(p.policy_ref) && p.may_own).map((p) => p.slug));
    setActing(it._id);
  };

  const action = async (id) => {
    const reason = (reasons[id] || "").trim();
    if (!reason) {
      needReason(id);
      return;
    }
    if (!chosen.length) {
      sayAt(id, "error", "Tick at least one policy.");
      return;
    }
    try {
      await axios.post(`${API}/policy-change-packs/scan-items/${id}/action`, { policies: chosen, reason },
        { headers: authHeaders() });
      setActing(null);
      const title = (items.find((i) => i._id === id) || {}).title || "The item";
      setNotice(`Added ${chosen.length} polic${chosen.length === 1 ? "y" : "ies"} to the change pack.`);
      setListMsg({ kind: "ok", text: `${title}: added ${chosen.length} polic${chosen.length === 1 ? "y" : "ies"} to the change pack.` });
      await load();
    } catch (err) {
      sayAt(id, "error", errorMessage(err, "Could not action the item"));
    }
  };

  const showLog = async () => {
    try {
      const r = await axios.get(`${BASE}/log`, { headers: authHeaders() });
      setLog(r.data);
    } catch (err) {
      setError(errorMessage(err, "Could not load the scan log"));
    }
  };

  const load = useCallback(async () => {
    try {
      const [s, i] = await Promise.all([
        axios.get(`${BASE}/status`, { headers: authHeaders() }),
        axios.get(`${BASE}/items`, { headers: authHeaders(), params: { status: "new" } }),
      ]);
      setData(s.data);
      setDraft(s.data.settings);
      setItems(i.data.items || []);
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load the horizon scan"));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    setError("");
    try {
      const r = await axios.post(`${BASE}/run`, {}, { headers: authHeaders() });
      setLastRun(r.data);
      await load();
    } catch (err) {
      setError(errorMessage(err, "The horizon scan could not run"));
    } finally {
      setRunning(false);
    }
  };

  const saveSettings = async () => {
    try {
      await axios.put(`${BASE}/settings`, draft, { headers: authHeaders() });
      setSaved("Schedule saved.");
      await load();
    } catch (err) {
      setError(errorMessage(err, "Could not save the schedule"));
    }
  };

  const triage = async (id, status) => {
    const reason = (reasons[id] || "").trim();
    if (!reason) {
      needReason(id);
      return;
    }
    try {
      await axios.post(`${BASE}/items/${id}/triage`, { status, reason }, { headers: authHeaders() });
      // The item leaves the list, so say what happened where the list starts.
      const title = (items.find((i) => i._id === id) || {}).title || "The item";
      const label = (TRIAGE.find(([st]) => st === status) || [status, status])[1];
      setListMsg({ kind: "ok", text: `${title}: marked "${label}". It is in the scan log.` });
      await load();
    } catch (err) {
      sayAt(id, "error", errorMessage(err, "Could not triage the item"));
    }
  };

  if (!data && error) {
    return <div className="p-6 text-sm text-red-300" data-testid="hs-error">{error}</div>;
  }
  if (!data) return <div className="p-6 text-sm text-slate-400">Loading the horizon scan…</div>;

  const latest = lastRun || (data.runs || [])[0];
  // BM-H4025: by default only items where Claude suggested at least one policy.
  const hasPolicy = (it) => (it.suggestion?.policies || []).length > 0;
  const withPolicy = items.filter(hasPolicy);
  const noPolicy = items.filter((it) => it.suggestion && !hasPolicy(it));
  const waiting = items.filter((it) => !it.suggestion);
  const shown = showOthers ? items : withPolicy;
  const failed =(data.latest_results || lastRun?.results || []).filter((r) => r.ok === false);
  const manual = data.manual_sources || [];

  return (
    <div className="p-6 max-w-5xl space-y-5" data-testid="hs-page">
      <header className="flex flex-wrap items-center gap-3">
        <Radar size={20} className="text-sky-300" />
        <h1 className="text-xl font-semibold text-slate-100">Horizon scan</h1>
        <button type="button" onClick={runNow} disabled={running} data-testid="hs-run-now"
          className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-sky-700 hover:bg-sky-600 text-sm text-white disabled:opacity-60">
          <Play size={14} /> {running ? "Scanning… (up to a minute)" : "Run a horizon scan now"}
        </button>
        <a href="/compliance/change-pack" data-testid="hs-change-pack-link"
          className="px-3 py-1.5 rounded-md border border-slate-600 text-sm text-slate-200 hover:border-sky-400">
          Change pack
        </a>
      </header>
      <p className="text-sm text-slate-400 max-w-3xl">
        Reads the SRA, the Law Society Gazette, the Legal Services Board, AML, HMRC, the Land Registry, Companies House,
        the courts and legislation, SRA and Law Society first. The first read of each source records what is already
        there; after that, anything new is listed below with Claude's suggestion of which policies it touches.
        You decide what happens to each item.
      </p>
      {error ? <div className="text-sm text-red-300" data-testid="hs-error">{error}</div> : null}
      {notice ? (
        <div className="text-sm text-emerald-300" data-testid="hs-notice">
          {notice} <a href="/compliance/change-pack" className="underline">Open the change pack</a>
        </div>
      ) : null}

      <section className="p-3 rounded-md border border-slate-700/60 bg-slate-900/40 space-y-2">
        <h2 className="text-sm font-semibold text-slate-100">Latest scan</h2>
        {latest ? (
          <>
            <div className="text-xs text-slate-400">
              {when(latest.started_at)} · {latest.trigger === "schedule" ? "scheduled" : `run by ${latest.by}`}
            </div>
            <RunSummary run={latest} />
          </>
        ) : (
          <div className="text-sm text-slate-400" data-testid="hs-never-run">No scan has run yet.</div>
        )}
        {failed.length ? (
          <div className="text-xs text-amber-200 space-y-0.5" data-testid="hs-failed">
            <div className="flex items-center gap-1"><AlertTriangle size={12} /> Not read this time:</div>
            {failed.map((f) => <div key={f.source_id}>{f.name}: {f.error}</div>)}
          </div>
        ) : null}
        {manual.length ? (
          <div className="text-xs text-slate-300 space-y-0.5" data-testid="hs-manual">
            <div>Check by hand (these sites refuse automated readers):</div>
            {manual.map((m) => (
              <a key={m.source_id} href={m.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sky-300 underline">
                {m.name} <ExternalLink size={10} />
              </a>
            ))}
          </div>
        ) : null}
      </section>

      {draft ? (
        <section className="p-3 rounded-md border border-slate-700/60 bg-slate-900/40 space-y-2" data-testid="hs-schedule">
          <h2 className="text-sm font-semibold text-slate-100">Schedule</h2>
          <div className="flex flex-wrap items-center gap-2 text-sm text-slate-300">
            <label className="inline-flex items-center gap-1">
              <input type="checkbox" id="hs-enabled" checked={draft.enabled}
                onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
              Scan automatically every
            </label>
            <select id="hs-weekday" className={INPUT} value={draft.weekday}
              onChange={(e) => setDraft({ ...draft, weekday: Number(e.target.value) })}>
              {data.weekdays.map((d, i) => <option key={d} value={i}>{d}</option>)}
            </select>
            <span>at</span>
            <select id="hs-hour" className={INPUT} value={draft.hour}
              onChange={(e) => setDraft({ ...draft, hour: Number(e.target.value) })}>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
            </select>
            <span>UK time</span>
            <button type="button" onClick={saveSettings} data-testid="hs-save"
              className="px-2 py-1 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm">Save</button>
            {saved ? <span className="text-xs text-emerald-300">{saved}</span> : null}
          </div>
        </section>
      ) : null}

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-100">
          {showOthers ? `All new items (${items.length})` : `New items with a suggested policy (${withPolicy.length})`}
        </h2>
        {noPolicy.length || waiting.length ? (
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400" data-testid="hs-others">
            <span>
              {showOthers ? "Showing every item, including " : "Not shown: "}
              {noPolicy.length} Claude found no policy for, {waiting.length} still waiting for Claude's suggestion.
            </span>
            <button type="button" onClick={() => setShowOthers(!showOthers)} data-testid="hs-toggle-others"
              className="underline text-sky-300">
              {showOthers ? "Show only items with a suggested policy" : "Show them"}
            </button>
            {waiting.length ? (
              <button type="button" onClick={suggestMissing} disabled={suggesting} data-testid="hs-suggest-missing"
                className="px-2 py-0.5 rounded-md border border-slate-600 text-slate-200 disabled:opacity-60">
                {suggesting ? "Asking Claude…" : `Ask Claude about the ${waiting.length} waiting`}
              </button>
            ) : null}
          </div>
        ) : null}
        {listMsg ? (
          // Fixed to the corner of the screen, so it is seen wherever the page is scrolled to.
          <div data-testid="hs-list-msg" role="status"
            className={`fixed bottom-4 right-4 z-50 max-w-md p-3 rounded-md shadow-lg text-sm border flex items-start gap-2 ${
              listMsg.kind === "error" ? "bg-red-950 border-red-700 text-red-100"
                : listMsg.kind === "info" ? "bg-slate-900 border-sky-700 text-sky-100"
                  : "bg-emerald-950 border-emerald-700 text-emerald-100"}`}>
            <span className="flex-1">{listMsg.text}</span>
            <button type="button" onClick={() => setListMsg(null)} className="text-xs opacity-70" aria-label="Close">✕</button>
          </div>
        ) : null}
        {shown.length === 0 ? (
          <div className="text-sm text-slate-400" data-testid="hs-no-items">
            No items waiting. {latest ? "" : "Run a scan to begin."}
          </div>
        ) : (
          shown.map((it) => {
            const s = it.suggestion || {};
            return (
              <article key={it._id} className="p-3 rounded-md border border-slate-700/60 bg-slate-900/40 space-y-1.5"
                data-testid={`hs-item-${it._id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  {s.relevance ? (
                    <span className={`px-2 py-0.5 rounded-full text-xs ${RELEVANCE[s.relevance] || RELEVANCE.low}`}>
                      {s.relevance} relevance
                    </span>
                  ) : <span className="px-2 py-0.5 rounded-full text-xs bg-slate-800 text-slate-400">no suggestion</span>}
                  <span className="text-xs text-slate-400">{it.source_name} · {when(it.retrieved_date)}</span>
                </div>
                <a href={it.url} target="_blank" rel="noreferrer" className="text-sm text-sky-300 underline">{it.title}</a>
                {s.reason ? (
                  <div className="text-xs text-slate-300">
                    {(s.policies || []).length ? <span className="font-mono">{s.policies.join(", ")}: </span> : null}
                    {s.reason}
                  </div>
                ) : null}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <input id={`hs-reason-${it._id}`} className={`${INPUT} flex-1 min-w-[12rem]`} placeholder="Reason"
                    value={reasons[it._id] || ""} onChange={(e) => {
                      setReasons({ ...reasons, [it._id]: e.target.value });
                      if (itemMsg[it._id]) setItemMsg((m) => ({ ...m, [it._id]: null }));
                    }} />
                  <button type="button" onClick={() => suggestReason(it._id)} disabled={reasonBusy === it._id}
                    data-testid={`hs-suggest-reason-${it._id}`} aria-label="Suggest a reason"
                    title={acting === it._id ? "Suggest a reason from this item and the policies ticked" : "Suggest a reason from this item"}
                    className="p-1.5 rounded-md border border-indigo-700 text-indigo-300 hover:border-indigo-400 disabled:opacity-60">
                    {reasonBusy === it._id ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  </button>
                  {TRIAGE.map(([st, label]) => (
                    <button key={st} type="button" onClick={() => triage(it._id, st)}
                      data-testid={`hs-triage-${st}-${it._id}`}
                      className="px-2 py-1 rounded-md border border-slate-600 text-xs text-slate-200 hover:border-sky-400">
                      {label}
                    </button>
                  ))}
                  <button type="button" onClick={() => startAction(it)} data-testid={`hs-action-${it._id}`}
                    className="px-2 py-1 rounded-md bg-sky-800 hover:bg-sky-700 text-xs text-white">
                    Action: change a policy
                  </button>
                </div>
                {itemMsg[it._id] ? (
                  <div data-testid={`hs-item-msg-${it._id}`} role="alert"
                    className={`text-xs ${itemMsg[it._id].kind === "error" ? "text-red-300" : "text-emerald-300"}`}>
                    {itemMsg[it._id].text}
                  </div>
                ) : null}
                {acting === it._id ? (
                  <div className="p-2 rounded-md border border-sky-800 bg-slate-900/60 space-y-2" data-testid={`hs-action-panel-${it._id}`}>
                    <div className="text-xs text-slate-300">
                      Which policies need to change? Claude's suggestion is ticked; correct it. Each goes into the open change pack.
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-56 overflow-auto">
                      {policies.map((p) => (
                        <label key={p.slug} className={`text-xs inline-flex items-center gap-1 ${p.may_own ? "text-slate-200" : "text-slate-500"}`}
                          title={p.may_own ? "" : "Only the policy's owner can action it"}>
                          <input type="checkbox" disabled={!p.may_own} checked={chosen.includes(p.slug)}
                            data-testid={`hs-policy-${it._id}-${p.slug}`}
                            onChange={(e) => setChosen(e.target.checked ? [...chosen, p.slug] : chosen.filter((s) => s !== p.slug))} />
                          {p.policy_ref} {p.name}
                        </label>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => action(it._id)} data-testid={`hs-action-confirm-${it._id}`}
                        className="px-2 py-1 rounded-md bg-sky-700 hover:bg-sky-600 text-xs text-white">
                        Add to the change pack
                      </button>
                      <button type="button" onClick={() => setActing(null)} className="px-2 py-1 text-xs text-slate-400">Cancel</button>
                    </div>
                    {itemMsg[it._id] ? (
                      <div className={`text-xs ${itemMsg[it._id].kind === "error" ? "text-red-300" : "text-emerald-300"}`} data-testid={`hs-action-msg-${it._id}`}>{itemMsg[it._id].text}</div>
                    ) : null}
                  </div>
                ) : null}
              </article>
            );
          })
        )}
      </section>

      <section className="space-y-1" data-testid="hs-log-section">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-slate-100">Horizon scan log</h2>
          <button type="button" onClick={showLog} data-testid="hs-show-log"
            className="px-2 py-0.5 rounded-md border border-slate-600 text-xs text-slate-200">
            {log ? "Refresh" : "Show the log"}
          </button>
        </div>
        {log ? (
          <div className="overflow-x-auto">
            <div className="text-xs text-slate-400 mb-1">
              Every decision on every item, newest first ({log.entries.length} decision{log.entries.length === 1 ? "" : "s"} across {log.items_examined} item{log.items_examined === 1 ? "" : "s"} read).
            </div>
            <table className="w-full text-xs text-slate-300" data-testid="hs-log">
              <thead className="text-slate-500 text-left">
                <tr><th className="py-1 pr-3">When</th><th className="pr-3">By</th><th className="pr-3">Decision</th>
                  <th className="pr-3">Item</th><th>Reason</th></tr>
              </thead>
              <tbody>
                {log.entries.map((e, n) => (
                  <tr key={`${e.item_id}-${n}`} className="border-t border-slate-800">
                    <td className="py-1 pr-3 whitespace-nowrap">{when(e.at)}</td>
                    <td className="pr-3">{e.by}</td>
                    <td className="pr-3">{e.decision}{e.before_log ? " (last decision only, before the log began)" : ""}</td>
                    <td className="pr-3"><a href={e.url} target="_blank" rel="noreferrer" className="text-sky-300 underline">{e.title}</a></td>
                    <td>{e.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <section className="space-y-1">
        <h2 className="text-sm font-semibold text-slate-100">Recent scans</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-slate-300" data-testid="hs-runs">
            <thead className="text-slate-500 text-left">
              <tr><th className="py-1 pr-3">When</th><th className="pr-3">By</th><th className="pr-3">Result</th>
                <th className="pr-3">Sources read</th><th>New items</th></tr>
            </thead>
            <tbody>
              {(data.runs || []).map((r) => (
                <tr key={r._id} className="border-t border-slate-800">
                  <td className="py-1 pr-3 whitespace-nowrap">{when(r.started_at)}</td>
                  <td className="pr-3">{r.trigger === "schedule" ? "Schedule" : r.by}</td>
                  <td className="pr-3">{(STATUS[r.status] || [r.status])[0]}</td>
                  <td className="pr-3">{r.sources_ok ?? 0} of {r.sources_total ?? 0}</td>
                  <td>{r.items_new ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
